import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TransactionApiItem } from "./userTransactionType";

export type FetchMode = "replace" | "append";

export interface FetchTransactionsPayload {
  pageNumber?: number;
  pageSize?: number;
  mode?: FetchMode;
  mobileNo?: string;
  fromDate?: string;
  toDate?: string;
  orderId?: string;
  txnReferenceId?: string;
}

export interface TransactionsState {
  items: TransactionApiItem[];

  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;

  loading: boolean;
  error: string | null;

  // Helps avoid unnecessary refetches if redux-persist rehydrates items
  initialized: boolean;
  lastUpdatedAt: string | null;

  // Filters for pagination
  filters: {
    mobileNo?: string;
    fromDate?: string;
    toDate?: string;
    orderId?: string;
    txnReferenceId?: string;
  };
}

const initialState: TransactionsState = {
  items: [],

  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false,

  loading: false,
  error: null,

  initialized: false,
  lastUpdatedAt: null,
  filters: {},
};

function mergeUniqueById(
  existing: TransactionApiItem[],
  incoming: TransactionApiItem[],
): TransactionApiItem[] {
  const map = new Map<string, TransactionApiItem>();
  for (const it of existing) map.set(it.bbpsBillPaymentId, it);
  for (const it of incoming) map.set(it.bbpsBillPaymentId, it);

  // keep newest first by createdAt
  return Array.from(map.values()).sort((a, b) => {
    const ta = Date.parse(a.createdAt);
    const tb = Date.parse(b.createdAt);
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

export const transactionsSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    fetchTransactionsRequest(
      state,
      action: PayloadAction<FetchTransactionsPayload>,
    ) {
      state.loading = true;
      state.error = null;

      // Update filters only if they are provided (e.g. on search start)
      // If we are appending (paging), we keep the old filters.
      if (action.payload.mode === "replace") {
        state.filters = {
          mobileNo: action.payload.mobileNo,
          fromDate: action.payload.fromDate,
          toDate: action.payload.toDate,
          orderId: action.payload.orderId,
          txnReferenceId: action.payload.txnReferenceId,
        };
      }
    },

    fetchTransactionsSuccess(
      state,
      action: PayloadAction<{
        mode: FetchMode;
        items: TransactionApiItem[];
        pageNumber: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
        message?: string;
      }>,
    ) {
      const {
        mode,
        items,
        pageNumber,
        pageSize,
        totalCount,
        totalPages,
        hasNext,
        hasPrev,
      } = action.payload;

      state.items =
        mode === "append" ? mergeUniqueById(state.items, items) : items;

      state.pageNumber = pageNumber;
      state.pageSize = pageSize;
      state.totalCount = totalCount;
      state.totalPages = totalPages;
      state.hasNext = hasNext;
      state.hasPrev = hasPrev;

      state.loading = false;
      state.error = null;

      state.initialized = true;
      state.lastUpdatedAt = new Date().toISOString();
    },

    fetchTransactionsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload || "Failed to fetch transactions.";
      state.initialized = true;
    },

    resetTransactions() {
      return initialState;
    },

    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
    },
  },
});

export const {
  fetchTransactionsRequest,
  fetchTransactionsSuccess,
  fetchTransactionsFailure,
  resetTransactions,
  setPageSize,
} = transactionsSlice.actions;

export default transactionsSlice.reducer;
